import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faClipboardList,
  faFilter,
  faEllipsisVertical,
  faEye,
  faPencil,
  faCopy,
  faArchive,
  faCodeBranch,
  faClockRotateLeft,
  faTrash,
  faCheckDouble,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useHasPermission } from '@/hooks/use-permissions';
import { useDebounce } from '@/hooks/use-debounce';
import { SkeletonPage } from '@/components/skeletons';
import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm, confirmDelete } from '@/lib/swal';
import { cn } from '@/lib/cn';
import { useProcedureList } from './hooks/useProcedureTemplates';
import {
  useDeleteProcedure,
  usePublishProcedure,
  useArchiveProcedure,
  useNewVersionProcedure,
} from './hooks/useProcedureMutations';
import { ProcedureStatusBadge } from './components/ProcedureStatusBadge';
import { ProcedureTypeBadge } from './components/ProcedureTypeBadge';
import { CloneDialog, type CloneSource } from './components/CloneDialog';
import { ROUTES } from '@/routes/routes';
import type {
  ProcedureStatus,
  ProcedureType,
  ProcedureTemplateListItem,
} from '@/services/procedure-template.service';

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

const PAGE_SIZE = 20;

// ── Row component (owns its mutations) ────────────────────────────────────────

interface ProcedureRowProps {
  item: ProcedureTemplateListItem;
  can: (p: string) => boolean;
  onCloneClick: (item: ProcedureTemplateListItem) => void;
}

function ProcedureRow({ item, can, onCloneClick }: ProcedureRowProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const publishMutation = usePublishProcedure(item.id);
  const archiveMutation = useArchiveProcedure(item.id);
  const newVersionMutation = useNewVersionProcedure();
  const deleteMutation = useDeleteProcedure();

  const goTo = () => {
    if (item.status === 'DRAFT' && can('procedure:update')) {
      navigate(ROUTES.PROCEDURE_EDIT.replace(':id', item.id));
    } else {
      navigate(ROUTES.PROCEDURE_VIEW.replace(':id', item.id));
    }
  };

  const handlePublish = async () => {
    const ok = await swalConfirm({
      title: `Publish '${item.title}'?`,
      text: 'This will lock the version from further editing.',
    });
    if (!ok) return;
    try {
      await publishMutation.mutateAsync();
      toast.success('Procedure published');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to publish'));
    }
  };

  const handleArchive = async () => {
    const ok = await swalConfirm({
      title: `Archive '${item.title}'?`,
      text: 'This removes it from new inspection assignments.',
    });
    if (!ok) return;
    try {
      await archiveMutation.mutateAsync();
      toast.success('Procedure archived');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to archive'));
    }
  };

  const handleNewVersion = async () => {
    const ok = await swalConfirm({
      title: 'Create New Version?',
      html: `<p class="text-sm">A new draft (v${item.versionNumber + 1}) will be created from the published v${item.versionNumber}.</p>`,
    });
    if (!ok) return;
    try {
      const result = await newVersionMutation.mutateAsync(item.id);
      toast.success(`Draft v${result.versionNumber} created`);
      navigate(ROUTES.PROCEDURE_EDIT.replace(':id', result.id));
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to create version'));
    }
  };

  const handleDelete = async () => {
    const ok = await confirmDelete(item.title);
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(item.id);
      toast.success('Procedure deleted');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to delete'));
    }
  };

  const menuItems = [
    {
      label: 'View',
      icon: faEye,
      action: () => navigate(ROUTES.PROCEDURE_VIEW.replace(':id', item.id)),
      show: true,
    },
    {
      label: 'Edit',
      icon: faPencil,
      action: () => navigate(ROUTES.PROCEDURE_EDIT.replace(':id', item.id)),
      show: item.status === 'DRAFT' && can('procedure:update'),
    },
    {
      label: 'Publish',
      icon: faCheckDouble,
      action: () => void handlePublish(),
      show: item.status === 'DRAFT' && can('procedure:publish'),
    },
    {
      label: 'Archive',
      icon: faArchive,
      action: () => void handleArchive(),
      show: item.status === 'PUBLISHED' && can('procedure:archive'),
    },
    {
      label: 'New Version',
      icon: faCodeBranch,
      action: () => void handleNewVersion(),
      show: item.status === 'PUBLISHED' && can('procedure:clone'),
    },
    {
      label: 'Clone',
      icon: faCopy,
      action: () => onCloneClick(item),
      show: can('procedure:clone'),
    },
    {
      label: 'Version History',
      icon: faClockRotateLeft,
      action: () => navigate(ROUTES.PROCEDURE_VERSIONS.replace(':id', item.id)),
      show: true,
    },
    {
      label: 'Delete',
      icon: faTrash,
      action: () => void handleDelete(),
      show: item.status === 'DRAFT' && can('procedure:delete'),
      danger: true,
    },
  ] as const;

  const actionsMenu = (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted transition-colors"
      >
        <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-9 z-20 min-w-[160px] rounded-xl border border-border bg-card py-1 shadow-lg">
            {menuItems
              .filter((m) => m.show)
              .map((m) => (
                <button
                  key={m.label}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    m.action();
                  }}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-4 py-2 text-sm transition-colors',
                    'danger' in m && m.danger
                      ? 'text-red-600 hover:bg-red-500/10'
                      : 'text-foreground hover:bg-muted',
                  )}
                >
                  <FontAwesomeIcon icon={m.icon} className="h-3.5 w-3.5 shrink-0" />
                  {m.label}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop row */}
      <tr
        className="hidden border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer transition-colors md:table-row"
        onClick={goTo}
      >
        <td className="px-4 py-3 font-medium text-foreground">{item.title}</td>
        <td className="px-4 py-3">
          <ProcedureTypeBadge procedureType={item.procedureType} />
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <ProcedureStatusBadge status={item.status} />
            {item.activePublishedVersion && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                Active: v{item.activePublishedVersion.versionNumber}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-muted-foreground">v{item.versionNumber}</td>
        <td className="px-4 py-3 text-muted-foreground">
          {item.equipmentModel
            ? `${item.equipmentModel.manufacturer.name} ${item.equipmentModel.name}`
            : '—'}
        </td>
        <td className="px-4 py-3 text-center text-muted-foreground">
          {item.totalSections}s / {item.totalSteps}
        </td>
        <td className="px-4 py-3 text-muted-foreground">{fmtDate(item.publishedAt)}</td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {actionsMenu}
        </td>
      </tr>

      {/* Mobile card */}
      <div className="rounded-xl border border-border bg-card p-4 md:hidden" onClick={goTo}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium text-foreground">{item.title}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <ProcedureTypeBadge procedureType={item.procedureType} />
              <ProcedureStatusBadge status={item.status} />
              <span className="text-xs text-muted-foreground">v{item.versionNumber}</span>
              {item.activePublishedVersion && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  Active: v{item.activePublishedVersion.versionNumber}
                </span>
              )}
            </div>
            {item.equipmentModel && (
              <p className="mt-1 text-xs text-muted-foreground">
                {item.equipmentModel.manufacturer.name} {item.equipmentModel.name}
              </p>
            )}
          </div>
          <div onClick={(e) => e.stopPropagation()}>{actionsMenu}</div>
        </div>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ProcedureLibraryPage() {
  const can = useHasPermission();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProcedureStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<ProcedureType | ''>('');
  const [cloneSource, setCloneSource] = useState<CloneSource | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  const filters = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch.length >= 2 ? debouncedSearch : undefined,
    status: statusFilter || undefined,
    procedureType: typeFilter || undefined,
    versionScope: 'CURRENT_ONLY' as const,
  };

  const { data, isLoading } = useProcedureList(filters);

  const hasActiveFilters = !!statusFilter || !!typeFilter || debouncedSearch.length >= 2;

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
    setPage(1);
  };

  if (isLoading) return <SkeletonPage />;

  const items = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Procedure Library</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage step-by-step procedure templates for inspections and service
          </p>
        </div>
        {can('procedure:create') && (
          <Button onClick={() => navigate(ROUTES.PROCEDURE_NEW)} className="min-h-[44px]">
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
            New Procedure
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search procedures…"
          className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-64"
        />
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as ProcedureType | '');
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All Types</option>
          <option value="INSPECTION">Inspection</option>
          <option value="SERVICE">Service</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ProcedureStatus | '');
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="PUBLISHED">Published</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} className="h-3 w-3" />
            Clear filters
          </button>
        )}
      </div>

      {/* Empty: no data at all */}
      {items.length === 0 && !hasActiveFilters && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <FontAwesomeIcon
            icon={faClipboardList}
            className="mb-4 h-10 w-10 text-muted-foreground/40"
          />
          <p className="text-base font-medium text-foreground">
            No procedure templates created yet
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create your first procedure to guide engineers through inspections and service
            activities.
          </p>
          {can('procedure:create') && (
            <Button onClick={() => navigate(ROUTES.PROCEDURE_NEW)} className="mt-5 min-h-[44px]">
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
              New Procedure
            </Button>
          )}
        </div>
      )}

      {/* Empty: filter miss */}
      {items.length === 0 && hasActiveFilters && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <FontAwesomeIcon icon={faFilter} className="mb-4 h-8 w-8 text-muted-foreground/40" />
          <p className="text-base font-medium text-foreground">No procedures match your filters</p>
          <p className="mt-1 text-sm text-muted-foreground">Try adjusting the search or filters.</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-4 text-sm font-medium text-primary hover:underline"
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* Desktop table */}
      {items.length > 0 && (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-border md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Title', 'Type', 'Status', 'Ver', 'Model', 'Steps', 'Published', ''].map(
                    (h) => (
                      <th
                        key={h}
                        className={cn(
                          'px-4 py-3 text-left font-semibold text-foreground',
                          h === 'Steps' && 'text-center',
                        )}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <ProcedureRow key={item.id} item={item} can={can} onCloneClick={setCloneSource} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {items.map((item) => (
              <ProcedureRow key={item.id} item={item} can={can} onCloneClick={setCloneSource} />
            ))}
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      {cloneSource && <CloneDialog source={cloneSource} onClose={() => setCloneSource(null)} />}
    </div>
  );
}
